import { palette } from "./tokens";
import { isLight, seeded } from "./seed";

/**
 * Square avatar with a generated face — a few strokes for eyes and a mouth.
 *
 * The alternative to the ring avatars: less decorative, far more legible at a
 * glance, and it gives each seat a small character rather than a pattern. Fits
 * a game where the whole point is reading the person.
 *
 * Deterministic from the seed, same as the rings.
 */

const FACE_COLORS = [
  "#E8A33C",
  "#6F8C3F",
  "#2E7D74",
  "#C9612B",
  "#B0803A",
  "#8C5A3C",
  "#4A7A9B",
  "#A6484F",
  "#D9C27E",
  "#C98B6B",
] as const;

/** Eyes are drawn as a pair; the same shape on both sides reads as a face. */
const EYES = [
  // dots
  (x: number) => `M${x - 2} 18 a2 2 0 1 0 4 0 a2 2 0 1 0 -4 0`,
  // vertical slits
  (x: number) => `M${x} 15 L${x} 21`,
  // closed / squint
  (x: number) => `M${x - 3} 18 L${x + 3} 18`,
  // wide open ring
  (x: number) => `M${x - 3} 18 a3 3 0 1 0 6 0 a3 3 0 1 0 -6 0`,
  // raised, looking up
  (x: number) => `M${x - 3} 19 q3 -5 6 0`,
  // cross / done with this
  (x: number) => `M${x - 2.5} 15.5 L${x + 2.5} 20.5 M${x + 2.5} 15.5 L${x - 2.5} 20.5`,
] as const;

const MOUTHS = [
  "M16 31 q8 6 16 0", // grin
  "M16 33 q8 -6 16 0", // frown
  "M16 32 L32 32", // flat
  "M20 30 h8 v5 h-8 z", // open rectangle
  "M18 31 l4 4 l4 -4 l4 4", // zigzag
  "M22 32 a2.5 2.5 0 1 0 5 0 a2.5 2.5 0 1 0 -5 0", // small o
  "M16 31 q4 5 8 0 q4 -5 8 0", // wavy
] as const;

export const AvatarFace = ({
  seed,
  size = 48,
  label,
}: {
  seed: string;
  size?: number;
  label?: string;
}) => {
  const rng = seeded(seed);
  const bg = rng.pick(FACE_COLORS);
  const ink = isLight(bg) ? palette.bg : palette.ink;

  const eye = rng.pick(EYES);
  const mouth = rng.pick(MOUTHS);
  // Occasional asymmetry — one eye different — is what makes a set of these
  // read as funny rather than uniform.
  const oddEye = rng.next() < 0.35 ? rng.pick(EYES) : eye;
  const brow = rng.next() < 0.4;

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 48 48"
      role={label ? "img" : "presentation"}
      aria-label={label}
      aria-hidden={label ? undefined : true}
    >
      <rect width="48" height="48" rx="6" fill={bg} />

      <g
        stroke={ink}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      >
        {brow && <path d="M12 12 L20 10" />}
        {brow && <path d="M28 10 L36 12" />}
        <path d={eye(16)} />
        <path d={oddEye(32)} />
        <path d={mouth} />
      </g>
    </svg>
  );
};
